import React, { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { 
  Search, Plus, Calendar, Mail, 
  ChevronRight, Sparkles, FolderOpen, Users,
  LayoutGrid, ArrowRight, Send, CheckCircle,
  FileEdit, Zap, Star, Play, Home, Inbox,
  BarChart3, Timer, FileText, Building2,
  Clock, AlertCircle, TrendingUp, Target,
  Briefcase, Scale, MousePointer, Download, Shield
} from 'lucide-react';

/**
 * HeroAnimatedV3 - WORLD CLASS animated landing with storytelling
 * 
 * Features:
 * - 3D Parallax & Mouse Tracking
 * - Interactive particles that follow cursor
 * - Morphing logo animations
 * - Sound design toggle
 * - Clickable progress journey map
 * - Premium micro-interactions
 * - Magnetic hover effects
 * - Ripple click effects
 * - Floating particles in background
 * - AI scanning effect on Inbox
 * - Typewriter effect on Document
 * - Chat streaming from top
 * - Social proof on Hero
 * - Animated gradient background
 * - Dramatic pacing
 */

// ═══════════════════════════════════════════════════════════════════
// MOUSE TRACKING CONTEXT - For 3D parallax effects
// ═══════════════════════════════════════════════════════════════════
interface MousePosition {
  x: number;
  y: number;
  normalizedX: number;
  normalizedY: number;
}

const MouseContext = createContext<MousePosition>({ x: 0, y: 0, normalizedX: 0, normalizedY: 0 });

const useMousePosition = () => useContext(MouseContext);

// ═══════════════════════════════════════════════════════════════════
// MAGNETIC BUTTON COMPONENT - Premium hover effect
// ═══════════════════════════════════════════════════════════════════
const MagneticButton = ({ 
  children, 
  className = '', 
  onClick,
  strength = 0.3 
}: { 
  children: React.ReactNode; 
  className?: string; 
  onClick?: () => void;
  strength?: number;
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const springConfig = { damping: 15, stiffness: 150 };
  const xSpring = useSpring(x, springConfig);
  const ySpring = useSpring(y, springConfig);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * strength);
    y.set((e.clientY - centerY) * strength);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
    setIsHovered(false);
  };

  return (
    <motion.button
      ref={ref}
      style={{ x: xSpring, y: ySpring }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      className={className}
      whileTap={{ scale: 0.97 }}
    >
      {children}
    </motion.button>
  );
};

// ═══════════════════════════════════════════════════════════════════
// RIPPLE EFFECT COMPONENT
// ═══════════════════════════════════════════════════════════════════
const RippleButton = ({ 
  children, 
  className = '', 
  onClick 
}: { 
  children: React.ReactNode; 
  className?: string; 
  onClick?: () => void;
}) => {
  const [ripples, setRipples] = useState<Array<{ x: number; y: number; id: number }>>([]);
  
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    
    setRipples(prev => [...prev, { x, y, id }]);
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== id));
    }, 600);
    
    onClick?.();
  };

  return (
    <button className={`relative overflow-hidden ${className}`} onClick={handleClick}>
      {ripples.map(ripple => (
        <motion.span
          key={ripple.id}
          initial={{ scale: 0, opacity: 0.5 }}
          animate={{ scale: 4, opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="absolute w-12 h-12 bg-white/30 rounded-full pointer-events-none"
          style={{ left: ripple.x - 24, top: ripple.y - 24 }}
        />
      ))}
      {children}
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════
// 3D TILT CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════
const TiltCard = ({ 
  children, 
  className = '',
  intensity = 10 
}: { 
  children: React.ReactNode; 
  className?: string;
  intensity?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  
  const springConfig = { damping: 20, stiffness: 200 };
  const rotateXSpring = useSpring(rotateX, springConfig);
  const rotateYSpring = useSpring(rotateY, springConfig);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const percentX = (e.clientX - centerX) / (rect.width / 2);
    const percentY = (e.clientY - centerY) / (rect.height / 2);
    rotateX.set(-percentY * intensity);
    rotateY.set(percentX * intensity);
  };

  const handleMouseLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ 
        rotateX: rotateXSpring, 
        rotateY: rotateYSpring,
        transformStyle: 'preserve-3d',
        transformPerspective: 1000
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// INTERACTIVE CURSOR PARTICLES
// ═══════════════════════════════════════════════════════════════════
const CursorParticles = ({ mousePos }: { mousePos: MousePosition }) => {
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; size: number }>>([]);
  const lastPos = useRef({ x: 0, y: 0 });
  
  useEffect(() => {
    const distance = Math.hypot(mousePos.x - lastPos.current.x, mousePos.y - lastPos.current.y);
    if (distance > 30) {
      const id = Date.now();
      setParticles(prev => [...prev.slice(-15), { 
        id, 
        x: mousePos.x, 
        y: mousePos.y,
        size: Math.random() * 8 + 4
      }]);
      lastPos.current = { x: mousePos.x, y: mousePos.y };
    }
  }, [mousePos.x, mousePos.y]);

  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {particles.map(particle => (
        <motion.div
          key={particle.id}
          initial={{ scale: 1, opacity: 0.6, x: particle.x, y: particle.y }}
          animate={{ scale: 0, opacity: 0, y: particle.y - 50 }}
          transition={{ duration: 1, ease: "easeOut" }}
          onAnimationComplete={() => {
            setParticles(prev => prev.filter(p => p.id !== particle.id));
          }}
          className="absolute rounded-full bg-purple-500"
          style={{ 
            width: particle.size, 
            height: particle.size,
            boxShadow: '0 0 10px rgba(147, 51, 234, 0.5)'
          }}
        />
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// GLOWING ORB THAT FOLLOWS CURSOR
// ═══════════════════════════════════════════════════════════════════
const CursorGlow = ({ mousePos }: { mousePos: MousePosition }) => {
  const x = useSpring(mousePos.x, { damping: 25, stiffness: 200 });
  const y = useSpring(mousePos.y, { damping: 25, stiffness: 200 });

  return (
    <motion.div
      className="fixed pointer-events-none z-40"
      style={{ 
        x, 
        y,
        translateX: '-50%',
        translateY: '-50%'
      }}
    >
      <div 
        className="w-[300px] h-[300px] rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(147, 51, 234, 0.4) 0%, transparent 70%)',
          filter: 'blur(40px)'
        }}
      />
    </motion.div>
  );
};

type Scene = 'intro' | 'dashboard' | 'inbox' | 'ai-chat' | 'document' | 'word-plugin' | 'time-tracking' | 'hero';

interface SceneContent {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  bullets: string[];
  sceneNumber: number;
}

// ═══════════════════════════════════════════════════════════════════
// INTERACTIVE PROGRESS JOURNEY MAP
// ═══════════════════════════════════════════════════════════════════
const JourneyProgressMap = ({ 
  currentScene, 
  onSceneClick,
  scenes 
}: { 
  currentScene: number;
  onSceneClick: (sceneNumber: number) => void;
  scenes: Array<{ number: number; label: string; icon: React.ReactNode }>;
}) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 mb-2">
        {scenes.map((scene, idx) => (
          <motion.button
            key={scene.number}
            onClick={() => onSceneClick(scene.number)}
            className="relative group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              className={`h-1.5 w-8 rounded-full transition-all duration-500 cursor-pointer ${
                scene.number <= currentScene 
                  ? 'bg-purple-500 shadow-lg shadow-purple-500/30' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
              animate={scene.number === currentScene ? { 
                boxShadow: ['0 0 10px rgba(147, 51, 234, 0.3)', '0 0 20px rgba(147, 51, 234, 0.5)', '0 0 10px rgba(147, 51, 234, 0.3)']
              } : {}}
              transition={{ duration: 2, repeat: Infinity }}
            />
            
            {/* Tooltip on hover */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              whileHover={{ opacity: 1, y: 0, scale: 1 }}
              className="absolute top-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
            >
              <div className="bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                {scene.label}
              </div>
            </motion.div>
          </motion.button>
        ))}
      </div>
      <p className="text-xs text-gray-500">
        Step {currentScene} of {scenes.length} 
        <span className="text-purple-400 ml-2 opacity-60">• Click to jump</span>
      </p>
    </div>
  );
};

// Typewriter Component
const Typewriter = ({ text, delay = 0, speed = 30, className = '' }: { 
  text: string; 
  delay?: number; 
  speed?: number;
  className?: string;
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const startTimer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(startTimer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    
    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
      }
    }, speed);
    
    return () => clearInterval(interval);
  }, [started, text, speed]);

  return (
    <span className={className}>
      {displayedText}
      {started && displayedText.length < text.length && (
        <motion.span 
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="inline-block w-0.5 h-5 bg-gray-400 ml-0.5 align-middle"
        />
      )}
    </span>
  );
};

// StreamingText Component - More dramatic streaming effect
const StreamingText = ({ text, delay = 0, className = '' }: { 
  text: string; 
  delay?: number; 
  className?: string;
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [started, setStarted] = useState(false);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const startTimer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(startTimer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    
    let index = 0;
    const chars = text.split('');
    const interval = setInterval(() => {
      if (index < chars.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
        setComplete(true);
      }
    }, 50); // Faster streaming
    
    return () => clearInterval(interval);
  }, [started, text]);

  return (
    <span className={className}>
      {displayedText.split('').map((char, idx) => (
        <motion.span
          key={idx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.1 }}
          style={{ display: 'inline-block' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
      {started && !complete && (
        <motion.span 
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
          className="inline-block w-0.5 h-[1em] bg-purple-500 ml-1 align-middle"
        />
      )}
    </span>
  );
};

// Animated Gradient Background
const AnimatedGradient = () => (
  <motion.div 
    className="absolute inset-0 pointer-events-none"
    animate={{
      background: [
        'radial-gradient(ellipse at 20% 20%, rgba(147, 51, 234, 0.08) 0%, transparent 50%)',
        'radial-gradient(ellipse at 80% 80%, rgba(147, 51, 234, 0.08) 0%, transparent 50%)',
        'radial-gradient(ellipse at 50% 20%, rgba(147, 51, 234, 0.08) 0%, transparent 50%)',
        'radial-gradient(ellipse at 20% 20%, rgba(147, 51, 234, 0.08) 0%, transparent 50%)',
      ]
    }}
    transition={{
      duration: 15,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  />
);

// ═══════════════════════════════════════════════════════════════════
// WORLD-CLASS INTRO COMPONENTS
// ═══════════════════════════════════════════════════════════════════

// Ambient particles that float in darkness - ENHANCED with mouse interaction
const AmbientParticles = ({ mousePos }: { mousePos?: MousePosition }) => {
  const particles = useMemo(() => Array.from({ length: 60 }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 20 + 15,
    delay: Math.random() * 5,
    magneticStrength: Math.random() * 0.5 + 0.2,
  })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => {
        // Calculate magnetic pull toward cursor
        const pullX = mousePos ? (mousePos.normalizedX - 0.5) * particle.magneticStrength * 30 : 0;
        const pullY = mousePos ? (mousePos.normalizedY - 0.5) * particle.magneticStrength * 30 : 0;
        
        return (
          <motion.div
            key={particle.id}
            className="absolute rounded-full"
            style={{
              width: particle.size,
              height: particle.size,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              background: 'rgba(147, 51, 234, 0.6)',
              boxShadow: '0 0 6px rgba(147, 51, 234, 0.4)',
            }}
            animate={{
              y: [0 + pullY, -30 + pullY, 0 + pullY],
              x: [0 + pullX, 15 + pullX, 0 + pullX],
              opacity: [0.1, 0.5, 0.1],
              scale: [1, 1.4, 1],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
              ease: "easeInOut",
            }}
          />
        );
      })}
      
      {/* Extra large glowing orbs */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={`orb-${i}`}
          className="absolute rounded-full"
          style={{
            width: 100 + i * 40,
            height: 100 + i * 40,
            left: `${15 + i * 18}%`,
            top: `${20 + i * 12}%`,
            background: `radial-gradient(circle, rgba(147, 51, 234, ${0.08 - i * 0.01}) 0%, transparent 70%)`,
            filter: 'blur(20px)',
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10 + i * 3,
            repeat: Infinity,
            delay: i * 2,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Typewriter with light trail effect
const LightTypewriter = ({ 
  text, 
  delay = 0, 
  onComplete,
  className = '' 
}: { 
  text: string; 
  delay?: number; 
  onComplete?: () => void;
  className?: string;
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const startTimer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(startTimer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayedText(text.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
        onComplete?.();
      }
    }, 60);

    return () => clearInterval(interval);
  }, [started, text, onComplete]);

  // Cursor blink
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setCursorVisible(v => !v);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <span className={className}>
      {displayedText.split('').map((char, idx) => (
        <motion.span
          key={idx}
          initial={{ opacity: 0, textShadow: '0 0 20px rgba(147, 51, 234, 0.8)' }}
          animate={{ opacity: 1, textShadow: '0 0 0px rgba(147, 51, 234, 0)' }}
          transition={{ duration: 0.5 }}
          style={{ display: 'inline-block' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
      <motion.span
        animate={{ opacity: cursorVisible ? 1 : 0 }}
        className="inline-block w-[3px] h-[1em] bg-purple-500 ml-1 align-middle"
        style={{ boxShadow: '0 0 10px rgba(147, 51, 234, 0.6)' }}
      />
    </span>
  );
};

// Particle explosion effect
const ParticleExplosion = ({ active, onComplete }: { active: boolean; onComplete?: () => void }) => {
  const [particles, setParticles] = useState<Array<{id: number; x: number; y: number; angle: number; speed: number; size: number}>>([]);

  useEffect(() => {
    if (active) {
      const newParticles = Array.from({ length: 60 }, (_, i) => ({
        id: i,
        x: 50,
        y: 50,
        angle: (i / 60) * Math.PI * 2,
        speed: Math.random() * 50 + 30,
        size: Math.random() * 4 + 2,
      }));
      setParticles(newParticles);
      
      setTimeout(() => {
        onComplete?.();
      }, 1500);
    }
  }, [active, onComplete]);

  if (!active) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-purple-500"
          style={{
            width: p.size,
            height: p.size,
            left: '50%',
            top: '50%',
            boxShadow: '0 0 10px rgba(147, 51, 234, 0.8)',
          }}
          initial={{ 
            x: 0, 
            y: 0, 
            opacity: 1,
            scale: 1 
          }}
          animate={{ 
            x: Math.cos(p.angle) * p.speed * 10,
            y: Math.sin(p.angle) * p.speed * 10,
            opacity: 0,
            scale: 0.5
          }}
          transition={{ 
            duration: 1.5, 
            ease: [0.16, 1, 0.3, 1] 
          }}
        />
      ))}
    </div>
  );
};

// Logo reveal with pulse - smooth entrance, no jump - ENHANCED with morphing
const LogoReveal = ({ visible }: { visible: boolean }) => {
  if (!visible) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 1, 
        ease: [0.16, 1, 0.3, 1]
      }}
      className="relative flex flex-col items-center"
    >
      {/* Subtle outer glow */}
      <motion.div
        animate={{
          opacity: [0.3, 0.5, 0.3],
          scale: [1, 1.05, 1],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute w-32 h-32 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(147, 51, 234, 0.3) 0%, transparent 70%)',
          filter: 'blur(20px)',
        }}
      />
      
      {/* Main Logo Icon */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 flex items-center justify-center shadow-2xl"
        style={{ 
          boxShadow: '0 0 40px rgba(147, 51, 234, 0.5)'
        }}
      >
        {/* Inner highlight */}
        <div className="absolute inset-1.5 rounded-xl bg-gradient-to-br from-white/20 to-transparent" />
        
        <motion.div
          animate={{ 
            filter: [
              'drop-shadow(0 0 5px rgba(255,255,255,0.3))',
              'drop-shadow(0 0 15px rgba(255,255,255,0.6))',
              'drop-shadow(0 0 5px rgba(255,255,255,0.3))',
            ]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          <Sparkles className="w-10 h-10 text-white" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Floating Particles Component - ENHANCED with interactive elements
const FloatingParticles = ({ mousePos }: { mousePos?: MousePosition }) => {
  const particles = useMemo(() => Array.from({ length: 40 }, (_, i) => ({
    id: i,
    size: Math.random() * 8 + 3,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 15 + 10,
    delay: Math.random() * 3,
    type: Math.random() > 0.7 ? 'glow' : 'normal',
  })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className={`absolute rounded-full ${
            particle.type === 'glow' 
              ? 'bg-purple-400/40' 
              : 'bg-purple-400/20'
          }`}
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            boxShadow: particle.type === 'glow' 
              ? '0 0 15px rgba(147, 51, 234, 0.4)' 
              : 'none',
          }}
          animate={{
            y: [0, -50, 0],
            x: [0, 25, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut",
          }}
        />
      ))}
      
      {/* Larger accent orbs with blur */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={`accent-${i}`}
          className="absolute rounded-full"
          style={{
            width: 60 + i * 20,
            height: 60 + i * 20,
            left: `${10 + i * 12}%`,
            top: `${15 + (i % 3) * 25}%`,
            background: `radial-gradient(circle, rgba(147, 51, 234, ${0.1 - i * 0.01}) 0%, transparent 70%)`,
            filter: 'blur(15px)',
          }}
          animate={{
            y: [0, -40, 0],
            x: [0, 20, 0],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 12 + i * 2,
            repeat: Infinity,
            delay: i * 0.8,
            ease: "easeInOut",
          }}
        />
      ))}
      
      {/* Shooting stars effect */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={`star-${i}`}
          className="absolute h-0.5 bg-gradient-to-r from-purple-500 to-transparent rounded-full"
          style={{
            width: 60 + i * 20,
            top: `${20 + i * 30}%`,
          }}
          initial={{ left: '-10%', opacity: 0 }}
          animate={{ 
            left: ['0%', '110%'],
            opacity: [0, 1, 1, 0]
          }}
          transition={{
            duration: 2,
            delay: 5 + i * 8,
            repeat: Infinity,
            repeatDelay: 15,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

const sceneContents: Record<Scene, SceneContent> = {
  intro: {
    icon: <Sparkles className="w-6 h-6" />,
    title: "",
    subtitle: "",
    description: "",
    bullets: [],
    sceneNumber: 0,
  },
  dashboard: {
    icon: <LayoutGrid className="w-6 h-6" />,
    title: "Command Center",
    subtitle: "Your entire practice, one glance",
    description: "Every case. Every deadline. Every client. See what matters most the moment you start your day.",
    bullets: [
      "Real-time case tracking",
      "Never miss a deadline", 
      "Intelligent prioritization"
    ],
    sceneNumber: 1,
  },
  inbox: {
    icon: <Mail className="w-6 h-6" />,
    title: "Intelligent Inbox",
    subtitle: "Email that works for you",
    description: "Watch as Lexy reads, understands, and organizes every email. Automatically linked to the right case, instantly prioritized.",
    bullets: [
      "Instant AI analysis",
      "Auto-linked to cases",
      "Smart action suggestions"
    ],
    sceneNumber: 2,
  },
  'ai-chat': {
    icon: <Sparkles className="w-6 h-6" />,
    title: "Ask Lexy Anything",
    subtitle: "Your legal AI, on demand",
    description: "Need a contract? A legal opinion? Just ask. Lexy delivers legally precise documents in seconds, not hours.",
    bullets: [
      "Plain language input",
      "Legally accurate output",
      "Instant delivery"
    ],
    sceneNumber: 3,
  },
  document: {
    icon: <FileEdit className="w-6 h-6" />,
    title: "Instant Documents",
    subtitle: "Draft to done, in seconds",
    description: "A complete employment contract, ready for review. Built on current law, tailored to your needs, legally sound.",
    bullets: [
      "Always compliant",
      "Fully customized",
      "Signature-ready"
    ],
    sceneNumber: 4,
  },
  'word-plugin': {
    icon: <FileText className="w-6 h-6" />,
    title: "Lexy for Word",
    subtitle: "AI power, native experience",
    description: "Your AI assistant lives inside Microsoft Word. Review contracts, get suggestions, and make changes without switching apps.",
    bullets: [
      "Native Word integration",
      "Real-time AI insights",
      "Seamless collaboration"
    ],
    sceneNumber: 5,
  },
  'time-tracking': {
    icon: <Timer className="w-6 h-6" />,
    title: "Effortless Billing",
    subtitle: "Time tracked. Invoices sent.",
    description: "Lexy captures every minute automatically. Generate professional invoices in one click and get paid faster.",
    bullets: [
      "Automatic time capture",
      "One-click invoicing",
      "Revenue analytics"
    ],
    sceneNumber: 6,
  },
  hero: {
    icon: <Sparkles className="w-6 h-6" />,
    title: "Legal work",
    subtitle: "simplified",
    description: "",
    bullets: [],
    sceneNumber: 7,
  },
};

const TOTAL_SCENES = 7;

// Scene definitions for journey map
const sceneDefinitions = [
  { number: 1, label: 'Dashboard', icon: <LayoutGrid className="w-3 h-3" /> },
  { number: 2, label: 'Inbox', icon: <Mail className="w-3 h-3" /> },
  { number: 3, label: 'AI Chat', icon: <Sparkles className="w-3 h-3" /> },
  { number: 4, label: 'Documents', icon: <FileEdit className="w-3 h-3" /> },
  { number: 5, label: 'Word Plugin', icon: <FileText className="w-3 h-3" /> },
  { number: 6, label: 'Billing', icon: <Timer className="w-3 h-3" /> },
];

// Sidebar items
const sidebarItems = [
  { icon: Home, label: 'Overview', active: false },
  { icon: Inbox, label: 'Inbox', badge: 3 },
  { icon: LayoutGrid, label: 'Work Queue' },
  { icon: Sparkles, label: 'AI Workspace' },
  { divider: true, label: 'CASES' },
  { icon: FolderOpen, label: 'Cases' },
  { icon: Calendar, label: 'Calendar' },
  { icon: Users, label: 'Clients' },
  { divider: true, label: 'ADMIN' },
  { icon: Timer, label: 'Time Tracking' },
  { icon: FileText, label: 'Invoicing' },
  { icon: BarChart3, label: 'Statistics' },
];

// ═══════════════════════════════════════════════════════════════════
// WORLD CLASS LANDING PAGE - Complete redesign
// ═══════════════════════════════════════════════════════════════════

// Animated word cycling for hero headline
const AnimatedWords = ({ words, className = '' }: { words: string[]; className?: string }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  
  useEffect(() => {
    const currentWord = words[currentIndex];
    
    if (isTyping && !isDeleting) {
      // Typing phase
      if (displayText.length < currentWord.length) {
        const timeout = setTimeout(() => {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        }, 80); // Typing speed
        return () => clearTimeout(timeout);
      } else {
        // Finished typing, wait then start deleting
        const timeout = setTimeout(() => {
          setIsDeleting(true);
        }, 2000); // Pause before deleting
        return () => clearTimeout(timeout);
      }
    } else if (isDeleting) {
      // Deleting phase
      if (displayText.length > 0) {
        const timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 40); // Deleting speed (faster)
        return () => clearTimeout(timeout);
      } else {
        // Finished deleting, move to next word
        setIsDeleting(false);
        setCurrentIndex((prev) => (prev + 1) % words.length);
      }
    }
  }, [displayText, isTyping, isDeleting, currentIndex, words]);

  return (
    <span className={className}>
      <span style={{ display: 'inline-block', minWidth: '320px', textAlign: 'left' }}>
        {displayText}
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.53, repeat: Infinity, repeatType: 'reverse' }}
          className="inline-block w-[4px] h-[0.85em] bg-gradient-to-b from-purple-400 to-violet-500 ml-0.5 rounded-sm"
          style={{ verticalAlign: 'baseline', marginBottom: '-0.1em' }}
        />
      </span>
    </span>
  );
};

// Floating Dashboard Mockup with 3D effect
const FloatingMockup = ({ mousePos }: { mousePos: MousePosition }) => {
  const rotateX = useTransform(
    useMotionValue(mousePos.normalizedY),
    [0, 1],
    [5, -5]
  );
  const rotateY = useTransform(
    useMotionValue(mousePos.normalizedX),
    [0, 1],
    [-5, 5]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 60, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
      style={{
        transformStyle: 'preserve-3d',
        perspective: 1000,
      }}
      className="relative"
    >
      {/* Glow behind mockup */}
      <div 
        className="absolute -inset-8 rounded-3xl opacity-40"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(147, 51, 234, 0.3) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      
      {/* Main mockup container */}
      <motion.div
        animate={{
          rotateX: (mousePos.normalizedY - 0.5) * 8,
          rotateY: (mousePos.normalizedX - 0.5) * 8,
        }}
        transition={{ type: 'spring', stiffness: 100, damping: 30 }}
        className="relative bg-[#0F0F12] rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
        style={{ width: '700px', height: '450px' }}
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 py-3 bg-[#1a1a1f] border-b border-white/10">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="bg-white/5 rounded-lg px-4 py-1 text-xs text-gray-500">
              app.lexy.law
            </div>
          </div>
        </div>
        
        {/* App content preview */}
        <div className="flex h-[calc(100%-44px)]">
          {/* Sidebar */}
          <div className="w-48 bg-[#0F0F12] border-r border-white/5 p-3">
            <div className="flex items-center gap-2 mb-6 px-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-white text-sm font-medium">Lexy</span>
            </div>
            {/* Nav items */}
            <div className="space-y-1">
              {[
                { icon: Home, label: 'Overview', active: true },
                { icon: Inbox, label: 'Inbox', badge: 3 },
                { icon: FolderOpen, label: 'Cases' },
                { icon: Sparkles, label: 'AI Workspace' },
                { icon: Timer, label: 'Time' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + i * 0.05 }}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${
                    item.active ? 'bg-white/10 text-white' : 'text-gray-500'
                  }`}
                >
                  <item.icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto bg-purple-500/30 text-purple-300 text-[9px] px-1.5 py-0.5 rounded">
                      {item.badge}
                    </span>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Main content */}
          <div className="flex-1 p-6 overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-center mb-6"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 mx-auto mb-3 flex items-center justify-center">
                <span className="text-white text-sm font-medium">JD</span>
              </div>
              <h3 className="text-white text-lg font-medium">Good morning, John</h3>
              <p className="text-gray-500 text-xs">3 high priority cases today</p>
            </motion.div>
            
            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { label: 'Active Cases', value: '12' },
                { label: 'Due Today', value: '3' },
                { label: 'Unread', value: '8' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 + i * 0.1 }}
                  className="bg-white/5 rounded-xl p-3 text-center"
                >
                  <div className="text-white text-xl font-semibold">{stat.value}</div>
                  <div className="text-gray-500 text-[10px]">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            
            {/* Chat input */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4 }}
              className="bg-white/5 rounded-xl px-4 py-3 flex items-center gap-3"
            >
              <Plus className="w-4 h-4 text-gray-500" />
              <span className="text-gray-500 text-sm flex-1">Ask Lexy anything...</span>
              <div className="w-7 h-7 bg-purple-500 rounded-lg flex items-center justify-center">
                <ArrowRight className="w-3.5 h-3.5 text-white" />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
      
      {/* Floating elements */}
      <motion.div
        initial={{ opacity: 0, x: 50, y: -20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute -right-8 top-12 bg-white rounded-xl shadow-2xl p-4 w-56"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-purple-600" />
          </div>
          <span className="text-sm font-medium text-gray-900">AI Generated</span>
        </div>
        <p className="text-xs text-gray-500">Employment contract ready for review</p>
        <div className="mt-3 flex gap-2">
          <span className="text-[10px] px-2 py-1 bg-green-100 text-green-700 rounded-full">Compliant</span>
          <span className="text-[10px] px-2 py-1 bg-blue-100 text-blue-700 rounded-full">2 min ago</span>
        </div>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, x: -50, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute -left-6 bottom-16 bg-white rounded-xl shadow-2xl p-3 w-48"
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-medium text-gray-900">Time tracking active</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">Nordic Tech AS • 1h 23m</p>
      </motion.div>
    </motion.div>
  );
};

// World Class Landing Page Component
const WorldClassLandingPage = ({ 
  navigate, 
  mousePos 
}: { 
  navigate: (path: string) => void; 
  mousePos: MousePosition;
}) => {
  const [activeFeature, setActiveFeature] = useState(0);
  
  const features = [
    {
      icon: Sparkles,
      title: 'AI-Powered Workspace',
      description: 'Draft contracts, analyze documents, and get instant legal insights. Lexy understands context and delivers accurate results in seconds.',
      color: 'purple',
    },
    {
      icon: Mail,
      title: 'Intelligent Inbox',
      description: 'Every email automatically analyzed, categorized, and linked to the right case. Never lose track of client communication again.',
      color: 'blue',
    },
    {
      icon: FolderOpen,
      title: 'Case Management',
      description: 'All your cases, documents, and deadlines in one place. Smart organization that adapts to how you work.',
      color: 'emerald',
    },
    {
      icon: Timer,
      title: 'Effortless Time Tracking',
      description: 'Automatic time capture as you work. Generate invoices in one click and get detailed insights into your practice.',
      color: 'orange',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Connect your tools',
      description: 'Link your email, calendar, and document storage. Lexy integrates seamlessly with the tools you already use.',
    },
    {
      number: '02',
      title: 'Let AI organize',
      description: 'Lexy automatically categorizes emails, tracks deadlines, and keeps everything connected to the right case.',
    },
    {
      number: '03',
      title: 'Work smarter',
      description: 'Focus on what matters. Draft documents in seconds, never miss a deadline, and bill accurately.',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="fixed inset-0 z-40 overflow-y-auto overflow-x-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950"
    >
      {/* ═══════════════════════════════════════════════════════════════
          SECTION 1: HERO - Dark, dramatic, with floating mockup
      ═══════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col">
        {/* Background effects - Matching login page style */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Animated gradient orbs - matching login page */}
          <motion.div
            animate={{
              x: [0, 30, 0],
              y: [0, -20, 0],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-20 right-20 w-96 h-96 rounded-full bg-purple-500/20 blur-3xl"
          />
          <motion.div
            animate={{
              x: [0, -20, 0],
              y: [0, 30, 0],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-indigo-500/20 blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.1, 0.15, 0.1],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-violet-500/10 blur-3xl"
          />
        </div>

        {/* Navbar */}
        <motion.nav
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="relative z-20 px-6 lg:px-12 py-5 flex items-center justify-between"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-white">Lexy</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {['Features', 'How it works', 'Pricing'].map((item, i) => (
              <motion.a
                key={item}
                href={`#${item.toLowerCase().replace(' ', '-')}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 + i * 0.05 }}
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                {item}
              </motion.a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              onClick={() => navigate('/lexy/login')}
              className="text-sm font-medium text-gray-400 hover:text-white transition-colors px-4 py-2"
            >
              Log in
            </motion.button>
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/lexy/signup')}
              className="text-sm font-medium bg-white hover:bg-gray-100 text-gray-900 px-5 py-2.5 rounded-xl transition-colors shadow-lg"
            >
              Get Started Free
            </motion.button>
          </div>
        </motion.nav>

        {/* Hero content - WORLD CLASS centered layout */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 lg:px-16 pt-8 pb-16">
          {/* Centered headline section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-center max-w-4xl mx-auto mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-8"
            >
              <Zap className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-purple-300">AI-powered legal workspace</span>
            </motion.div>

            {/* Main headline - dramatic and centered */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6 tracking-tight">
              <span className="block bg-gradient-to-r from-purple-400 via-fuchsia-300 to-blue-400 bg-clip-text text-transparent pb-2">
                <AnimatedWords 
                  words={['Draft contracts', 'Analyze emails', 'Track time', 'Manage cases']} 
                  className=""
                />
              </span>
              <span className="block text-white">in seconds, not hours</span>
            </h1>

            <p className="text-xl text-gray-400 mb-10 leading-relaxed max-w-2xl mx-auto">
              The AI operating system for modern law firms. Everything you need to run your practice, powered by intelligence.
            </p>

            {/* CTA buttons - centered */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <motion.button
                whileHover={{ scale: 1.03, boxShadow: '0 0 40px rgba(147, 51, 234, 0.4)' }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/lexy/signup')}
                className="group flex items-center justify-center gap-2.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-semibold px-8 py-4 rounded-2xl transition-all shadow-xl shadow-purple-500/30"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03, backgroundColor: 'rgba(255,255,255,0.05)' }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-center gap-2.5 text-gray-300 hover:text-white font-medium px-8 py-4 rounded-2xl border border-white/10 hover:border-white/20 transition-all backdrop-blur-sm"
              >
                <Play className="w-5 h-5" />
                Watch Demo
              </motion.button>
            </div>

            {/* Trust badges - subtle */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-500">
              {['GDPR compliant', 'SOC 2 certified', 'No credit card'].map((item, i) => (
                <motion.span 
                  key={i} 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  {item}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Product mockup - hero visual */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl mx-auto"
          >
            {/* Glow effect behind mockup */}
            <div 
              className="absolute -inset-4 rounded-3xl"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(147, 51, 234, 0.2) 0%, transparent 70%)',
                filter: 'blur(60px)',
              }}
            />
            
            {/* Main browser mockup */}
            <motion.div
              animate={{
                rotateX: (mousePos.normalizedY - 0.5) * 4,
                rotateY: (mousePos.normalizedX - 0.5) * 4,
              }}
              transition={{ type: 'spring', stiffness: 100, damping: 30 }}
              style={{ transformStyle: 'preserve-3d', perspective: 1200 }}
              className="relative bg-[#12121a] rounded-2xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden"
            >
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#1a1a24] border-b border-white/5">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="flex items-center gap-2 bg-white/5 rounded-lg px-4 py-1.5">
                    <div className="w-3 h-3 rounded-full bg-emerald-500/60" />
                    <span className="text-xs text-gray-400">app.lexy.law</span>
                  </div>
                </div>
                <div className="w-16" /> {/* Spacer for balance */}
              </div>
              
              {/* App content - dashboard preview */}
              <div className="flex h-[420px]">
                {/* Sidebar */}
                <div className="w-56 bg-[#0f0f14] border-r border-white/5 p-4 flex flex-col">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white font-semibold">Lexy</span>
                  </div>
                  
                  {/* Nav items */}
                  <div className="space-y-1">
                    {[
                      { icon: Home, label: 'Overview', active: true },
                      { icon: Inbox, label: 'Inbox', badge: '3' },
                      { icon: FolderOpen, label: 'Cases' },
                      { icon: Sparkles, label: 'AI Workspace' },
                      { icon: Timer, label: 'Time Tracking' },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 + i * 0.05 }}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                          item.active 
                            ? 'bg-white/10 text-white' 
                            : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="flex-1">{item.label}</span>
                        {item.badge && (
                          <span className="bg-purple-500/30 text-purple-300 text-xs px-2 py-0.5 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* User section at bottom */}
                  <div className="mt-auto pt-4 border-t border-white/5">
                    <div className="flex items-center gap-3 px-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                        <span className="text-xs font-medium text-white">JD</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">John Doe</div>
                        <div className="text-xs text-gray-500">Partner</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Main content area */}
                <div className="flex-1 p-6 bg-slate-900/50">
                  {/* Welcome header */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="mb-6"
                  >
                    <h2 className="text-2xl font-semibold text-white mb-1">Good morning, John</h2>
                    <p className="text-gray-500">You have 3 high priority tasks today</p>
                  </motion.div>
                  
                  {/* Stats grid */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {[
                      { label: 'Active Cases', value: '12', trend: '+2' },
                      { label: 'Due Today', value: '3', trend: '' },
                      { label: 'Unread Messages', value: '8', trend: '+5' },
                    ].map((stat, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9 + i * 0.1 }}
                        className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/5"
                      >
                        <div className="flex items-end justify-between mb-1">
                          <span className="text-3xl font-bold text-white">{stat.value}</span>
                          {stat.trend && (
                            <span className="text-xs text-emerald-400">{stat.trend}</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{stat.label}</span>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* AI Chat input */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2 }}
                    className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/5"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-1 flex items-center gap-3">
                        <Plus className="w-5 h-5 text-gray-500" />
                        <span className="text-gray-400">Ask Lexy anything...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                          <FileText className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                          <ArrowRight className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Floating notification cards */}
            <motion.div
              initial={{ opacity: 0, x: 40, y: -20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 1.3, duration: 0.6 }}
              className="absolute -right-4 top-20 bg-white rounded-2xl shadow-2xl p-4 w-64 border border-gray-100"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 text-sm">AI Generated</div>
                  <p className="text-xs text-gray-500 mt-0.5">Employment contract ready for review</p>
                  <div className="flex gap-2 mt-2">
                    <span className="text-[10px] px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full font-medium">Compliant</span>
                    <span className="text-[10px] px-2 py-1 bg-gray-100 text-gray-600 rounded-full">2 min ago</span>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -40, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 1.5, duration: 0.6 }}
              className="absolute -left-4 bottom-24 bg-white rounded-2xl shadow-2xl p-4 w-56 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-semibold text-gray-900 text-sm">Time tracking active</span>
              </div>
              <p className="text-xs text-gray-500">Nordic Tech AS • Contract Review</p>
              <div className="mt-2 text-lg font-bold text-gray-900">1h 23m</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2"
          >
            <motion.div className="w-1.5 h-1.5 bg-white/60 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 2: HOW IT WORKS - Light section
      ═══════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="relative py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
              How it works
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Get started in minutes
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              No complex setup. No steep learning curve. Just connect and start working smarter.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                className="relative"
              >
                {/* Connector line */}
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-purple-200 to-transparent -translate-x-4" />
                )}
                
                <div className="text-6xl font-bold text-purple-100 mb-4">{step.number}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-500 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 3: FEATURES - Alternating light/dark cards
      ═══════════════════════════════════════════════════════════════ */}
      <section id="features" className="relative py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-gray-900 text-white rounded-full text-sm font-semibold mb-4">
              Features
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to run your practice
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Powerful tools designed specifically for legal professionals, powered by AI that understands your work.
            </p>
          </motion.div>

          {/* Feature tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {features.map((feature, idx) => (
              <motion.button
                key={idx}
                onClick={() => setActiveFeature(idx)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
                  activeFeature === idx
                    ? 'bg-gray-900 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <feature.icon className="w-4 h-4" />
                {feature.title}
              </motion.button>
            ))}
          </div>

          {/* Active feature detail */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFeature}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-gray-100"
            >
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className={`w-14 h-14 rounded-2xl bg-${features[activeFeature].color}-100 flex items-center justify-center mb-6`}
                    style={{ backgroundColor: activeFeature === 0 ? '#f3e8ff' : activeFeature === 1 ? '#dbeafe' : activeFeature === 2 ? '#d1fae5' : '#ffedd5' }}
                  >
                    {React.createElement(features[activeFeature].icon, { 
                      className: `w-7 h-7`,
                      style: { color: activeFeature === 0 ? '#9333ea' : activeFeature === 1 ? '#2563eb' : activeFeature === 2 ? '#059669' : '#ea580c' }
                    })}
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                    {features[activeFeature].title}
                  </h3>
                  <p className="text-lg text-gray-500 leading-relaxed mb-6">
                    {features[activeFeature].description}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/lexy/signup')}
                    className="inline-flex items-center gap-2 text-purple-600 font-semibold hover:text-purple-700 transition-colors"
                  >
                    Learn more
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </div>
                
                {/* Feature illustration placeholder */}
                <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl h-64 lg:h-80 flex items-center justify-center">
                  {React.createElement(features[activeFeature].icon, { 
                    className: 'w-20 h-20 text-gray-300'
                  })}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 4: CTA - Dark, dramatic - Matching login page style
      ═══════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 overflow-hidden">
        {/* Background effects - matching login page */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Ready to transform your
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                legal practice?
              </span>
            </h2>
            <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
              Join forward-thinking law firms already using Lexy to work smarter, bill accurately, and deliver better results for their clients.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 0 40px rgba(147, 51, 234, 0.4)' }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/lexy/signup')}
                className="w-full sm:w-auto group flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-900 font-semibold px-10 py-5 rounded-xl transition-all shadow-xl"
              >
                Start Free Trial
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/lexy/login')}
                className="w-full sm:w-auto text-gray-400 hover:text-white font-medium px-10 py-5 rounded-xl border border-white/10 hover:border-white/20 transition-all"
              >
                Log in
              </motion.button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
                { icon: CheckCircle, text: 'GDPR Compliant' },
                { icon: CheckCircle, text: 'SOC 2 Certified' },
                { icon: Zap, text: '99.9% Uptime' },
              ].map((badge, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.1 }}
                  className="flex items-center gap-2 text-gray-500"
                >
                  <badge.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{badge.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer - matching login page style */}
      <footer className="bg-slate-950 border-t border-white/5 py-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-semibold text-white">Lexy</span>
            </div>
            
            <div className="flex items-center gap-8 text-sm text-gray-500">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
              <span>© 2025 Lexy. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </motion.div>
  );
};

const HeroAnimatedV3 = () => {
  const navigate = useNavigate();
  // NOTE: Intro sequence disabled - start at phase 3 and 'hero' scene to skip intro
  const [phase, setPhase] = useState(3);
  const [currentScene, setCurrentScene] = useState<Scene>('hero');
  const [chatMessages, setChatMessages] = useState<Array<{type: 'user' | 'ai', text: string}>>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [stats, setStats] = useState({ cases: 0, clients: 0, tasks: 0, inbox: 0 });
  const [showSidebar, setShowSidebar] = useState(false);
  
  // New intro animation states
  // NOTE: Intro sequence disabled - set to 'complete' to skip directly to hero/landing page
  const [introPhase, setIntroPhase] = useState<'darkness' | 'question' | 'logo' | 'meetLexy' | 'complete'>('complete');
  const [showQuestion, setShowQuestion] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [showMeetLexy, setShowMeetLexy] = useState(false);
  
  // WORLD CLASS: Mouse tracking for parallax
  const [mousePos, setMousePos] = useState<MousePosition>({ x: 0, y: 0, normalizedX: 0.5, normalizedY: 0.5 });
  const [showCursorEffects, setShowCursorEffects] = useState(true);
  
  // Track mouse position globally
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({
        x: e.clientX,
        y: e.clientY,
        normalizedX: e.clientX / window.innerWidth,
        normalizedY: e.clientY / window.innerHeight
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  // Scene navigation function for journey map
  const jumpToScene = useCallback((sceneNumber: number) => {
    const sceneMap: Record<number, Scene> = {
      1: 'dashboard',
      2: 'inbox',
      3: 'ai-chat',
      4: 'document',
      5: 'word-plugin',
      6: 'time-tracking',
    };
    
    if (sceneMap[sceneNumber]) {
      setCurrentScene(sceneMap[sceneNumber]);
      if (sceneNumber === 1) animateStats();
    }
  }, []);

  // World-class intro sequence timing - DISABLED
  // NOTE: Entire intro sequence disabled - users go directly to landing page
  // To re-enable the intro, change initial states at the top of the component:
  // - introPhase: 'darkness' instead of 'complete'
  // - phase: 0 instead of 3
  // - currentScene: 'intro' instead of 'hero'
  // Then uncomment the useEffect below:
  /*
  useEffect(() => {
    const sequence = async () => {
      // Phase 1: Brief darkness with ambient particles (0-1s)
      await new Promise(r => setTimeout(r, 1000));
      
      // Phase 3: Logo reveal - go directly here
      setIntroPhase('logo');
      setShowLogo(true);
      await new Promise(r => setTimeout(r, 1500));
      
      // Phase 4: Meet Lexy
      setIntroPhase('meetLexy');
      setShowMeetLexy(true);
      await new Promise(r => setTimeout(r, 2500));
      
      // Go directly to hero/landing page (skip demo scenes)
      setIntroPhase('complete');
      setPhase(3);
      setCurrentScene('hero');
    };
    
    sequence();
  }, []);
  */

  // Track if skip was used
  const skipUsedRef = useRef(false);

  // Scene sequence - improved pacing with dramatic pauses
  // NOTE: Demo scenes temporarily disabled - intro now goes directly to hero
  // To re-enable demo sequence, set skipDemoSequence to false
  const skipDemoSequence = true;
  
  useEffect(() => {
    if (phase < 3) return;
    if (skipUsedRef.current) return; // Don't run if skip was used
    if (skipDemoSequence) return; // Skip demo scenes - go directly to hero

    const runSequence = async () => {
      // Scene 1: Dashboard - let it build
      setCurrentScene('dashboard');
      animateStats();
      await new Promise(r => setTimeout(r, 7000));

      // Scene 2: Inbox - longer for AI scanning effect
      setCurrentScene('inbox');
      await new Promise(r => setTimeout(r, 9000));

      // Scene 3: AI Chat - Extended dialogue
      setCurrentScene('ai-chat');
      await new Promise(r => setTimeout(r, 800));
      
      // User's first message
      setChatMessages([{ type: 'user', text: 'Create an employment contract for a senior developer, $95,000 salary, remote work allowed' }]);
      
      await new Promise(r => setTimeout(r, 1500));
      setIsTyping(true);
      
      await new Promise(r => setTimeout(r, 2000));
      setIsTyping(false);
      setChatMessages(prev => [...prev, { 
        type: 'ai', 
        text: 'I\'ll create a comprehensive employment contract. Based on current labor law for tech positions, I\'m including remote work provisions, IP assignment clauses, and standard benefits.' 
      }]);

      await new Promise(r => setTimeout(r, 2000));
      
      // User's follow-up
      setChatMessages(prev => [...prev, { 
        type: 'user', 
        text: 'Add a 90-day probation period and include non-compete clause' 
      }]);
      
      await new Promise(r => setTimeout(r, 1500));
      setIsTyping(true);
      
      await new Promise(r => setTimeout(r, 1800));
      setIsTyping(false);
      setChatMessages(prev => [...prev, { 
        type: 'ai', 
        text: 'Done. I\'ve added a 90-day probationary period with performance review, and a 12-month non-compete limited to direct competitors within your region. The contract is ready for review.' 
      }]);

      await new Promise(r => setTimeout(r, 2500));

      // Scene 4: Document - longer for typewriter effect
      setCurrentScene('document');
      await new Promise(r => setTimeout(r, 8000));

      // Scene 5: Word Plugin
      setCurrentScene('word-plugin');
      await new Promise(r => setTimeout(r, 8000));

      // Scene 6: Time Tracking & Invoicing
      setCurrentScene('time-tracking');
      await new Promise(r => setTimeout(r, 8000));

      // Scene 7: Hero (Final)
      setCurrentScene('hero');
    };

    runSequence();
  }, [phase]);

  const animateStats = () => {
    const targets = { cases: 12, clients: 28, tasks: 5, inbox: 3 };
    let step = 0;
    const interval = setInterval(() => {
      step++;
      const progress = Math.min(step / 25, 1);
      setStats({
        cases: Math.round(targets.cases * progress),
        clients: Math.round(targets.clients * progress),
        tasks: Math.round(targets.tasks * progress),
        inbox: Math.round(targets.inbox * progress),
      });
      if (step >= 25) clearInterval(interval);
    }, 60);
  };

  const currentContent = sceneContents[currentScene];

  // Sidebar Component
  const Sidebar = ({ animate = true }: { animate?: boolean }) => (
    <motion.div
      initial={animate ? { x: -220, opacity: 0 } : false}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="w-[220px] bg-[#0F0F12] flex flex-col p-4 flex-shrink-0"
    >
      {/* Logo */}
      <motion.div 
        initial={animate ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-2.5 mb-6 px-2"
      >
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <span className="text-white font-semibold text-base">Lexy</span>
      </motion.div>
      
      {/* Nav items */}
      <div className="flex-1">
        {sidebarItems.map((item, idx) => (
          <motion.div
            key={idx}
            initial={animate ? { opacity: 0, x: -20 } : false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + idx * 0.03 }}
          >
            {item.divider ? (
              <div className="text-[10px] uppercase tracking-wider text-gray-600 mt-5 mb-2 px-2">
                {item.label}
              </div>
            ) : (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm mb-0.5 text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors cursor-pointer">
                {item.icon && <item.icon className="w-4 h-4" />}
                <span>{item.label}</span>
                {item.badge && (
                  <span className="ml-auto bg-purple-500/30 text-purple-300 text-[10px] px-1.5 py-0.5 rounded">
                    {item.badge}
                  </span>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>
      
      {/* User */}
      <motion.div 
        initial={animate ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="pt-4 border-t border-white/10"
      >
        <div className="flex items-center gap-3 px-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
            <span className="text-white text-xs font-medium">JD</span>
          </div>
          <div>
            <div className="text-white text-sm font-medium">John Doe</div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-xs text-gray-500">Online</span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <MouseContext.Provider value={mousePos}>
    <div className="w-full h-screen bg-white overflow-hidden relative">
      
      {/* WORLD CLASS: Cursor glow effect */}
      {showCursorEffects && introPhase === 'complete' && (
        <CursorGlow mousePos={mousePos} />
      )}
      
      {/* WORLD CLASS: Cursor particles trail */}
      {showCursorEffects && introPhase !== 'complete' && (
        <CursorParticles mousePos={mousePos} />
      )}
      
      {/* Sound toggle removed */}

      {/* ═══════════════════════════════════════════════════════════════════
          WORLD-CLASS INTRO - "What if your legal work understood itself?"
      ═══════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {introPhase !== 'complete' && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ 
              opacity: 0, 
              scale: 1.05, 
              filter: 'blur(20px)',
              transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
            }}
            className="fixed inset-0 z-50 bg-[#0a0a0f] flex items-center justify-center overflow-hidden"
          >
            {/* ENHANCED: Ambient particles with mouse interaction */}
            <AmbientParticles mousePos={mousePos} />
            
            {/* Subtle grid pattern */}
            <div 
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(147, 51, 234, 0.3) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(147, 51, 234, 0.3) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px'
              }}
            />
            
            {/* Ambient glow in center */}
            <motion.div
              animate={{
                opacity: [0.1, 0.2, 0.1],
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute w-[600px] h-[600px] rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(147, 51, 234, 0.15) 0%, transparent 70%)',
              }}
            />
            
            {/* Main content container */}
            <div className="relative z-10 text-center px-6 max-w-4xl">
              
              {/* The Question: "What if your legal work understood itself?" */}
              <AnimatePresence>
                {showQuestion && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ 
                      opacity: 0,
                      scale: 0.95,
                      filter: 'blur(20px)',
                      transition: { duration: 0.6 }
                    }}
                    className="text-2xl sm:text-4xl lg:text-5xl xl:text-6xl font-light tracking-tight leading-snug"
                  >
                    <span className="text-white/90">What if your legal work</span>
                    <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-purple-300 to-purple-400">
                      <LightTypewriter 
                        text="understood itself?"
                        delay={1500}
                      />
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Logo reveal */}
              <AnimatePresence>
                {showLogo && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col items-center"
                  >
                    {/* Container with fixed height to prevent layout shift */}
                    <div className="flex flex-col items-center" style={{ minHeight: '280px' }}>
                      <LogoReveal visible={showLogo} />
                      
                      {/* Meet Lexy text - always takes space, fades in */}
                      <div className="mt-10 text-center" style={{ minHeight: '140px' }}>
                        <motion.h1 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: showMeetLexy ? 1 : 0 }}
                          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                          className="text-5xl sm:text-6xl font-semibold text-white mb-5 tracking-tight"
                        >
                          Meet{' '}
                          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-purple-300 to-purple-400">
                            Lexy
                          </span>
                        </motion.h1>
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: showMeetLexy ? 1 : 0 }}
                          transition={{ delay: 0.2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                          className="text-xl text-gray-400 max-w-md mx-auto"
                        >
                          The AI-powered operating system for modern law firms
                        </motion.p>
                        
                        {/* Subtle animated line */}
                        <motion.div
                          initial={{ scaleX: 0, opacity: 0 }}
                          animate={{ scaleX: showMeetLexy ? 1 : 0, opacity: showMeetLexy ? 1 : 0 }}
                          transition={{ delay: 0.4, duration: 0.8 }}
                          className="w-24 h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent mx-auto mt-8"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Skip button - bottom right */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2, duration: 0.5 }}
              onClick={() => {
                skipUsedRef.current = true;
                setIntroPhase('complete');
                setPhase(3);
                setCurrentScene('hero');
              }}
              className="absolute bottom-8 right-8 z-50 flex items-center gap-2 text-gray-500 hover:text-white text-sm font-medium px-4 py-2 rounded-full border border-white/10 hover:border-white/30 bg-white/5 hover:bg-white/10 backdrop-blur-sm transition-all duration-300"
            >
              Skip intro
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════
          MAIN LAYOUT - Completely different for mobile vs desktop
          Mobile: Full-screen storytelling with phone preview
          Desktop: Split-screen with full demo
      ═══════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {phase >= 2 && currentScene !== 'hero' && (
          <>

            {/* ═══════════════════════════════════════════════════════════════
                MAIN RESPONSIVE LAYOUT - Stack on mobile, side-by-side on desktop
            ═══════════════════════════════════════════════════════════════ */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-0 flex flex-col lg:flex-row"
            >
            {/* LEFT PANEL - Dark Storytelling (full width on mobile, fixed on desktop) */}
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="w-full lg:w-[380px] bg-[#0F0F12] flex flex-col flex-shrink-0"
            >
              {/* Logo + Mobile CTA */}
              <div className="px-6 lg:px-8 pt-6 lg:pt-8 pb-4">
                <div className="flex items-center justify-between mb-4 lg:mb-6">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white font-semibold text-base">Lexy</span>
                  </div>
                  {/* Mobile: Show CTA in header */}
                  <button 
                    onClick={() => navigate('/lexy/signup')}
                    className="lg:hidden text-xs text-purple-400 font-medium px-3 py-1.5 rounded-lg border border-purple-500/30"
                  >
                    Get Started
                  </button>
                </div>

                {/* Progress indicator - 6 scenes */}
                <div className="flex items-center gap-2 mb-2">
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <div
                      key={num}
                      className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                        num <= currentContent.sceneNumber 
                          ? 'bg-purple-500' 
                          : 'bg-white/10'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Step {currentContent.sceneNumber} of 6
                </p>
              </div>

              {/* Scene content - scrollable on mobile */}
              <div className="flex-1 px-6 lg:px-8 pb-6 lg:pb-8 overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentScene}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                  >
                    {/* Icon */}
                    {currentContent.icon && currentContent.title && (
                      <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4 lg:mb-6 text-purple-400">
                        {currentContent.icon}
                      </div>
                    )}

                    {/* Title */}
                    <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-2 tracking-tight">
                      {currentContent.title}
                    </h2>
                    
                    {/* Subtitle */}
                    <p className="text-base lg:text-lg text-purple-400 font-medium mb-4 lg:mb-6">
                      {currentContent.subtitle}
                    </p>

                    {/* Description - Hidden on small mobile */}
                    <p className="hidden sm:block text-gray-400 leading-relaxed mb-6 lg:mb-8 text-sm lg:text-[15px]">
                      {currentContent.description}
                    </p>

                    {/* Bullets */}
                    {currentContent.bullets.length > 0 && (
                      <div className="space-y-2 lg:space-y-3">
                        {currentContent.bullets.map((bullet, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 + idx * 0.15 }}
                            className="flex items-center gap-2 lg:gap-3"
                          >
                            <CheckCircle className="w-4 h-4 lg:w-5 lg:h-5 text-green-500 flex-shrink-0" />
                            <span className="text-sm lg:text-base text-gray-300">{bullet}</span>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Bottom CTA */}
              {/* Bottom CTA - responsive */}
              <div className="px-6 lg:px-8 py-4 lg:py-6 border-t border-white/10">
                <button 
                  onClick={() => navigate('/lexy/signup')}
                  className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-900 font-medium py-3 lg:py-3.5 rounded-xl transition-colors"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </button>
                <div className="flex items-center justify-center gap-3 lg:gap-4 mt-3 lg:mt-4 text-[10px] lg:text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-green-500" />
                    GDPR compliant
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-green-500" />
                    SOC 2 certified
                  </span>
                </div>
              </div>
            </motion.div>

            {/* RIGHT PANEL - Product Demo (Hidden on mobile) */}
            <div className="hidden lg:flex flex-1 bg-white flex-col overflow-hidden">

              {/* Demo content */}
              <div className="flex-1 p-6 overflow-hidden">
                <AnimatePresence mode="wait">
                  
                  {/* DASHBOARD SCENE - Matching actual app design */}
                  {currentScene === 'dashboard' && (
                    <motion.div
                      key="dashboard"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="h-full flex flex-col max-w-2xl mx-auto pt-8"
                    >
                      {/* Avatar - Simple, matching app */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                        className="flex flex-col items-center mb-4"
                      >
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-100 shadow-sm mb-3 bg-gray-100">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_legalchat-ui/artifacts/c74jdfz8_image.png" 
                            alt="Lexy"
                            className="w-full h-full object-cover scale-150 object-top"
                          />
                        </div>
                        
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-5 h-5 bg-black rounded-md flex items-center justify-center">
                            <Sparkles className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-base font-semibold text-gray-900">Lexy</span>
                        </div>
                        <p className="text-sm text-gray-400">Your AI legal assistant</p>
                      </motion.div>

                      {/* Greeting - Streaming */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.4 }}
                        className="text-center mb-8"
                      >
                        <StreamingText 
                          text="Good evening, Erik" 
                          className="text-3xl font-semibold text-gray-900 mb-2 block"
                          delay={800}
                        />
                        <motion.p 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 2.2 }}
                          className="text-gray-500"
                        >
                          Busy day ahead — 3 high priority cases.
                        </motion.p>
                      </motion.div>

                      {/* Two columns: Timeline + Priority */}
                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 2.8, duration: 0.5 }}
                        className="grid grid-cols-2 gap-12 mb-8"
                      >
                        {/* Timeline */}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Today</h3>
                          <div className="space-y-1">
                            {[
                              { time: '09:00', title: 'Team standup', location: 'Teams' },
                              { time: '11:30', title: 'Client call — Telenor', location: 'Teams' },
                              { time: '14:00', title: 'Contract review', location: 'Meeting room 2' }
                            ].map((item, idx) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -15 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 3 + idx * 0.12 }}
                                className="flex items-start gap-3 py-1.5 hover:bg-gray-50 -mx-2 px-2 rounded-lg cursor-pointer transition-colors"
                              >
                                <span className="text-sm text-gray-400 tabular-nums w-10">{item.time}</span>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{item.title}</p>
                                  <p className="text-xs text-gray-400">{item.location}</p>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>

                        {/* Priority Cases */}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Priority</h3>
                          <div className="space-y-1">
                            {[
                              { title: 'Shipping Contract Review', client: 'Fjord Shipping AS' },
                              { title: 'Patent Application', client: 'Nordic Tech AS' },
                              { title: 'Employment Dispute', client: 'Acme Corp' }
                            ].map((c, idx) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: 15 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 3.1 + idx * 0.12 }}
                                className="flex items-start gap-3 py-1.5 hover:bg-gray-50 -mx-2 px-2 rounded-lg cursor-pointer transition-colors"
                              >
                                <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{c.title}</p>
                                  <p className="text-xs text-gray-400 truncate">{c.client}</p>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </motion.div>

                      {/* Suggestions */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 3.8 }}
                        className="flex flex-wrap gap-4 justify-center mb-6"
                      >
                        {['What should I focus on?', 'Summarize my cases', 'Any urgent emails?'].map((s, i) => (
                          <span 
                            key={i} 
                            className="text-sm text-gray-400 hover:text-gray-900 cursor-pointer transition-colors"
                          >
                            {s} →
                          </span>
                        ))}
                      </motion.div>

                      {/* Quick actions + Chat input */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 4.2 }}
                        className="mt-auto"
                      >
                        <div className="flex items-center justify-center gap-6 mb-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1.5 hover:text-gray-900 cursor-pointer transition-colors">
                            <Plus className="w-4 h-4" /> New case
                          </span>
                          <span className="flex items-center gap-1.5 hover:text-gray-900 cursor-pointer transition-colors">
                            <Calendar className="w-4 h-4" /> Calendar
                          </span>
                          <span className="flex items-center gap-1.5 hover:text-gray-900 cursor-pointer transition-colors">
                            <Clock className="w-4 h-4" /> Time
                          </span>
                        </div>
                        
                        <div className="flex items-center bg-white rounded-full border border-gray-200 px-4 py-3">
                          <Plus className="w-5 h-5 text-gray-400 mr-3" />
                          <span className="flex-1 text-gray-400">Ask anything</span>
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                        
                        <p className="text-center text-xs text-gray-400 mt-2">
                          Lexy has access to all your cases
                        </p>
                      </motion.div>
                    </motion.div>
                  )}

                  {/* INBOX SCENE - Slide and scale from right */}
                  {currentScene === 'inbox' && (
                    <motion.div
                      key="inbox"
                      initial={{ opacity: 0, x: 100, scale: 0.95 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: -100, scale: 0.95, rotateY: -10 }}
                      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                      style={{ transformStyle: 'preserve-3d' }}
                      className="h-full flex gap-5"
                    >
                      {/* Email list - slides in from left */}
                      <motion.div 
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="w-[280px] bg-white rounded-xl border border-gray-100 flex flex-col flex-shrink-0 overflow-hidden"
                      >
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4 }}
                          className="px-4 py-4 border-b border-gray-100"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-gray-900">Inbox</h3>
                            <motion.span 
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.6, type: "spring" }}
                              className="bg-purple-100 text-purple-700 text-xs font-medium px-2.5 py-1 rounded-full"
                            >
                              11 new
                            </motion.span>
                          </div>
                          <div className="flex gap-2">
                            {['Important', 'All', 'Unread'].map((tab, i) => (
                              <motion.button 
                                key={i} 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 + i * 0.1 }}
                                className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${i === 0 ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                              >
                                {tab}
                              </motion.button>
                            ))}
                          </div>
                        </motion.div>
                        <div className="flex-1 overflow-auto">
                          {[
                            { name: 'James Richardson', subject: 'Urgent: Shareholder Agreement', time: '10:32', unread: true, starred: true },
                            { name: 'Sarah Mitchell', subject: 'M&A Due Diligence Documents', time: '09:15', unread: true },
                            { name: 'District Court', subject: 'Filing Confirmation', time: 'Yesterday', unread: true },
                            { name: 'Michael Chen', subject: 'Employment Contract Review', time: 'Yesterday' },
                          ].map((email, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, x: -30 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ 
                                delay: 0.7 + idx * 0.1,
                                duration: 0.4,
                                ease: [0.16, 1, 0.3, 1]
                              }}
                              className={`px-4 py-3.5 border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${idx === 0 ? 'bg-purple-50 border-l-3 border-l-purple-500' : ''}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  {email.unread && (
                                    <motion.div 
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      transition={{ delay: 0.9 + idx * 0.1, type: "spring" }}
                                      className="w-2 h-2 bg-purple-500 rounded-full" 
                                    />
                                  )}
                                  <span className={`text-sm ${email.unread ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                                    {email.name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  {email.starred && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                                  <span className="text-xs text-gray-400">{email.time}</span>
                                </div>
                              </div>
                              <p className={`text-sm truncate ${email.unread ? 'text-gray-700' : 'text-gray-500'}`}>
                                {email.subject}
                              </p>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>

                      {/* Email content - fades up */}
                      <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="flex-1 bg-white rounded-xl border border-gray-100 flex flex-col min-w-0 overflow-hidden"
                      >
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.8 }}
                          className="px-6 py-4 border-b border-gray-100"
                        >
                          <h2 className="font-semibold text-gray-900 text-lg mb-2">Urgent: Shareholder Agreement Review</h2>
                          <div className="flex items-center gap-3">
                            <motion.div 
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 1, type: "spring" }}
                              className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium"
                            >
                              JR
                            </motion.div>
                            <div>
                              <p className="font-medium text-gray-900">James Richardson</p>
                              <p className="text-sm text-gray-500">CFO, GlobalCorp Inc. • 10:32</p>
                            </div>
                          </div>
                        </motion.div>
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 1.1 }}
                          className="flex-1 p-6 text-gray-700 leading-relaxed overflow-auto"
                        >
                          <p className="mb-4">Dear Legal Team,</p>
                          <p className="mb-4">We need immediate review of the shareholder agreement for our Series B funding round.</p>
                          <p className="font-medium text-gray-900 mb-2">Key concerns:</p>
                          <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
                            <li>Anti-dilution provisions in Section 4.2</li>
                            <li>Board composition rights</li>
                            <li>Drag-along and tag-along clauses</li>
                          </ul>
                          <p className="mb-6">Deadline: <span className="text-red-600 font-semibold">Friday, December 13th</span></p>
                          
                          {/* Email Attachment */}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.3 }}
                            className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3 hover:bg-gray-100 transition-colors cursor-pointer group"
                          >
                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-red-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 truncate">Shareholder_Agreement_Draft_v2.pdf</p>
                              <p className="text-xs text-gray-500">2.4 MB • PDF Document</p>
                            </div>
                            <Download className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                          </motion.div>
                          
                          {/* Professional Email Signature */}
                          <div className="border-t border-gray-200 pt-5 mt-6">
                            <div className="flex items-start gap-4">
                              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                                JR
                              </div>
                              <div className="flex-1">
                                <p className="font-bold text-gray-900 text-lg">James Richardson</p>
                                <p className="text-blue-600 font-medium text-sm">Chief Financial Officer</p>
                                <p className="text-gray-600 text-sm mt-1">GlobalCorp Inc.</p>
                                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                                  <span className="flex items-center gap-1.5">
                                    <Mail className="w-3.5 h-3.5" />
                                    j.richardson@globalcorp.com
                                  </span>
                                  <span>|</span>
                                  <span>+1 (555) 234-5678</span>
                                </div>
                                <div className="flex items-center gap-2 mt-3">
                                  <div className="h-1 w-12 bg-blue-600 rounded" />
                                  <div className="h-1 w-8 bg-blue-400 rounded" />
                                  <div className="h-1 w-4 bg-blue-300 rounded" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>

                      {/* AI Analysis Panel - with scanning effect */}
                      <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="w-[260px] bg-white rounded-xl border-2 border-purple-200 flex flex-col flex-shrink-0 overflow-hidden relative"
                      >
                        {/* Scanning line effect */}
                        <motion.div
                          initial={{ top: 0 }}
                          animate={{ top: '100%' }}
                          transition={{ delay: 0.9, duration: 1.5, ease: "easeInOut" }}
                          className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent z-10"
                          style={{ boxShadow: '0 0 20px 5px rgba(147, 51, 234, 0.3)' }}
                        />
                        
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 1 }}
                          className="px-4 py-3 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-white"
                        >
                          <div className="flex items-center gap-2">
                            <motion.div
                              animate={{ rotate: [0, 360] }}
                              transition={{ delay: 0.9, duration: 1.5, ease: "linear" }}
                            >
                              <Sparkles className="w-4 h-4 text-purple-600" />
                            </motion.div>
                            <span className="font-semibold text-gray-900">Lexy AI</span>
                            <motion.span 
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 2.4, type: "spring" }}
                              className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-auto"
                            >
                              Analyzed
                            </motion.span>
                          </div>
                          {/* Analyzing text that changes */}
                          <motion.p
                            initial={{ opacity: 1 }}
                            animate={{ opacity: 0 }}
                            transition={{ delay: 2.4, duration: 0.3 }}
                            className="text-xs text-purple-600 mt-1"
                          >
                            Analyzing email content...
                          </motion.p>
                        </motion.div>
                        
                        <div className="flex-1 p-4 space-y-4 overflow-auto">
                          <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 2.5 }}
                          >
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">AI Summary</p>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              Urgent: Series B shareholder agreement needs review. 3 legal risk points identified. Deadline Friday.
                            </p>
                          </motion.div>

                          <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 2.8 }}
                          >
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">Extracted Info</p>
                            <div className="space-y-2">
                              {[
                                { label: 'Category', value: 'Corporate Law' },
                                { label: 'Priority', value: 'High', color: 'text-red-600' },
                                { label: 'Deadline', value: 'Dec 13, 2024' },
                              ].map((item, i) => (
                                <motion.div 
                                  key={i} 
                                  initial={{ opacity: 0, x: 10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 3.0 + i * 0.15 }}
                                  className="flex items-center justify-between text-sm"
                                >
                                  <span className="text-gray-500">{item.label}</span>
                                  <span className={item.color || 'text-gray-900'}>{item.value}</span>
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>

                          <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 3.5 }}
                          >
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">Suggested Actions</p>
                            <div className="space-y-1.5">
                              {['Create case', 'Add deadline', 'Assign to partner'].map((action, i) => (
                                <motion.div 
                                  key={i} 
                                  initial={{ opacity: 0, x: 10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 3.7 + i * 0.15 }}
                                  className="flex items-center gap-2 text-sm text-gray-700"
                                >
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                  {action}
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>
                        </div>

                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 4.2 }}
                          className="p-4 border-t border-gray-100"
                        >
                          <button className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 rounded-xl transition-colors">
                            <Sparkles className="w-4 h-4" />
                            Create Case
                          </button>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  )}

                  {/* AI CHAT SCENE - Zoom up with blur */}
                  {currentScene === 'ai-chat' && (
                    <motion.div
                      key="ai-chat"
                      initial={{ opacity: 0, scale: 0.9, y: 50 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 1.05, y: -30, filter: 'blur(4px)' }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full flex flex-col max-w-3xl mx-auto"
                    >
                      {/* Chat messages - from top */}
                      <div className="flex-1 pt-4 overflow-hidden">
                        <div className="space-y-6">
                          {chatMessages.map((msg, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, y: -20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4 }}
                              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              {msg.type === 'ai' && (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mr-4 flex-shrink-0">
                                  <Sparkles className="w-5 h-5 text-white" />
                                </div>
                              )}
                              <div className={`max-w-[70%] ${
                                msg.type === 'user' 
                                  ? 'bg-gray-900 text-white rounded-2xl rounded-tr-md px-5 py-3' 
                                  : ''
                              }`}>
                                <p className={msg.type === 'user' ? 'text-[15px]' : 'text-[17px] text-gray-800 leading-relaxed'}>
                                  {msg.text}
                                </p>
                              </div>
                            </motion.div>
                          ))}
                          
                          {isTyping && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-start">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mr-4">
                                <Sparkles className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex gap-1.5 py-3">
                                <div className="w-2.5 h-2.5 bg-gray-400 rounded-full animate-bounce" />
                                <div className="w-2.5 h-2.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2.5 h-2.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </div>
                            </motion.div>
                          )}
                        </div>
                      </div>
                      
                      {/* Input at bottom */}
                      <div className="mt-auto pb-4">
                        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl px-5 py-4 shadow-sm">
                          <span className="text-gray-400 flex-1">Ask Lexy anything...</span>
                          <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
                            <Send className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* DOCUMENT SCENE */}
                  {currentScene === 'document' && (
                    <motion.div
                      key="document"
                      initial={{ opacity: 0, scale: 0.85, rotateX: 15, y: 60 }}
                      animate={{ opacity: 1, scale: 1, rotateX: 0, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -40 }}
                      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                      style={{ transformStyle: 'preserve-3d' }}
                      className="h-full max-w-4xl mx-auto"
                    >
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm h-full flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <motion.div 
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.2 }}
                              className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center"
                            >
                              <FileEdit className="w-6 h-6 text-blue-600" />
                            </motion.div>
                            <div>
                              <motion.h3 
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 }}
                                className="font-semibold text-gray-900 text-lg"
                              >
                                Employment_Contract_Senior_Dev.docx
                              </motion.h3>
                              <motion.p 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="text-sm text-gray-500"
                              >
                                Generated by Lexy AI • Just now
                              </motion.p>
                            </div>
                          </div>
                          <motion.span 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.5, type: "spring" }}
                            className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-4 py-2 rounded-full"
                          >
                            <CheckCircle className="w-4 h-4" />
                            AI-generated
                          </motion.span>
                        </div>
                        
                        <div className="flex-1 p-8 overflow-auto">
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                            className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-8 flex items-center gap-3"
                          >
                            <motion.div
                              animate={{ rotate: [0, 10, -10, 0] }}
                              transition={{ delay: 0.8, duration: 0.5 }}
                            >
                              <Zap className="w-5 h-5 text-purple-600" />
                            </motion.div>
                            <span className="text-purple-700">AI is generating your contract...</span>
                          </motion.div>
                          
                          <motion.h1 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="text-3xl font-bold text-gray-900 mb-8 text-center"
                          >
                            EMPLOYMENT CONTRACT
                          </motion.h1>
                          
                          <div className="space-y-6 text-gray-700 leading-relaxed">
                            <motion.section
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 1.0 }}
                            >
                              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. PARTIES</h2>
                              <p>
                                <Typewriter 
                                  text="This employment agreement is entered into between:" 
                                  delay={1100} 
                                  speed={20}
                                />
                              </p>
                              <p className="mt-2">
                                <strong>Employer: </strong>
                                <Typewriter text="[Company Name Inc.]" delay={1800} speed={25} />
                              </p>
                              <p>
                                <strong>Employee: </strong>
                                <Typewriter text="[Employee Name]" delay={2300} speed={25} />
                              </p>
                            </motion.section>
                            
                            <motion.section
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 2.8 }}
                            >
                              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. POSITION</h2>
                              <p>
                                <Typewriter 
                                  text="Employee is hired as " 
                                  delay={2900} 
                                  speed={20}
                                />
                                <motion.span 
                                  initial={{ opacity: 0, backgroundColor: 'transparent' }}
                                  animate={{ opacity: 1, backgroundColor: 'rgb(254 240 138)' }}
                                  transition={{ delay: 3.5, duration: 0.5 }}
                                  className="px-1.5 py-0.5 rounded font-medium"
                                >
                                  <Typewriter text="Senior Developer" delay={3500} speed={40} />
                                </motion.span>
                              </p>
                            </motion.section>
                            
                            <motion.section
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 4.2 }}
                            >
                              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. COMPENSATION</h2>
                              <p>
                                <Typewriter text="Annual salary: " delay={4300} speed={20} />
                                <motion.span 
                                  initial={{ opacity: 0, backgroundColor: 'transparent' }}
                                  animate={{ opacity: 1, backgroundColor: 'rgb(254 240 138)' }}
                                  transition={{ delay: 4.8, duration: 0.5 }}
                                  className="px-1.5 py-0.5 rounded font-medium"
                                >
                                  <Typewriter text="$95,000" delay={4800} speed={50} />
                                </motion.span>
                              </p>
                              <p className="mt-2">
                                <Typewriter 
                                  text="Salary is paid on the last business day of each month." 
                                  delay={5300} 
                                  speed={15}
                                />
                              </p>
                            </motion.section>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* WORD PLUGIN SCENE - Enhanced */}
                  {currentScene === 'word-plugin' && (
                    <motion.div
                      key="word-plugin"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full"
                    >
                      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl h-full flex overflow-hidden">
                        {/* Word Document Area */}
                        <div className="flex-1 flex flex-col">
                          {/* Word Title Bar - Authentic Microsoft style */}
                          <div className="bg-[#217346] px-4 py-2">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                  <span className="text-white text-base font-bold">W</span>
                                </div>
                                <span className="text-white/95 text-sm font-semibold tracking-wide">Microsoft Word</span>
                              </div>
                              <div className="flex-1 text-center">
                                <span className="text-white/90 text-sm font-medium">NDA_TechCorp_Final.docx</span>
                              </div>
                              <motion.div 
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.5 }}
                                className="flex items-center gap-2 bg-white/25 px-4 py-1.5 rounded-full"
                              >
                                <motion.span
                                  animate={{ 
                                    boxShadow: ['0 0 0 0 rgba(74, 222, 128, 0.4)', '0 0 0 6px rgba(74, 222, 128, 0)', '0 0 0 0 rgba(74, 222, 128, 0.4)']
                                  }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                  className="w-2.5 h-2.5 rounded-full bg-green-400"
                                />
                                <span className="text-white text-xs font-semibold">Lexy Active</span>
                              </motion.div>
                            </div>
                          </div>
                          
                          {/* Ribbon Toolbar - Enhanced */}
                          <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50/80 flex items-center gap-8">
                            <div className="flex items-center gap-1 text-gray-500 text-xs">
                              {['File', 'Home', 'Insert'].map((tab, i) => (
                                <span key={i} className="px-3 py-1.5 hover:bg-gray-200 rounded cursor-pointer transition-colors">{tab}</span>
                              ))}
                              <span className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-purple-50 text-purple-700 rounded font-semibold border border-purple-200">
                                ✨ Lexy AI
                              </span>
                              {['Review', 'View'].map((tab, i) => (
                                <span key={i} className="px-3 py-1.5 hover:bg-gray-200 rounded cursor-pointer transition-colors">{tab}</span>
                              ))}
                            </div>
                          </div>

                          {/* Document content with sophisticated track changes */}
                          <div className="flex-1 p-8 overflow-auto bg-[#f4f5f6]">
                            <div className="bg-white shadow-md border border-gray-200 max-w-2xl mx-auto p-14 min-h-full rounded">
                              <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="space-y-6 text-gray-800 leading-relaxed"
                                style={{ fontFamily: 'Georgia, serif' }}
                              >
                                <h1 className="text-2xl font-bold text-center mb-12 tracking-wider text-gray-900">
                                  NON-DISCLOSURE AGREEMENT
                                </h1>

                                <motion.p
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 0.3 }}
                                  className="text-[15px] leading-7"
                                >
                                  This Non-Disclosure Agreement ("Agreement") is entered into as of{' '}
                                  <motion.span 
                                    initial={{ backgroundColor: 'transparent' }}
                                    animate={{ backgroundColor: 'rgb(220 252 231)' }}
                                    transition={{ delay: 0.8, duration: 0.4 }}
                                    className="text-green-800 px-1.5 py-0.5 rounded font-semibold"
                                  >
                                    December 16, 2025
                                  </motion.span>
                                  {' '}by and between the undersigned parties.
                                </motion.p>

                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 1.2 }}
                                  className="relative text-[15px] leading-7"
                                >
                                  <p>
                                    <span className="font-bold">2.1 Confidentiality Period.</span> The receiving party shall maintain confidentiality for a period of{' '}
                                    <motion.span 
                                      initial={{ opacity: 1 }}
                                      animate={{ opacity: 0.5 }}
                                      transition={{ delay: 1.8 }}
                                      className="line-through text-red-600 bg-red-50 px-1 rounded"
                                    >
                                      two (2) years
                                    </motion.span>
                                    {' '}
                                    <motion.span 
                                      initial={{ opacity: 0, scale: 0.9 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      transition={{ delay: 2.2, type: "spring" }}
                                      className="bg-green-100 text-green-800 px-2 py-0.5 rounded font-bold"
                                    >
                                      five (5) years
                                    </motion.span>
                                    {' '}from the date of disclosure.
                                  </p>
                                  
                                  {/* AI Suggestion Popup - Positioned below the text to not obstruct */}
                                  <motion.div
                                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{ delay: 2.5, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    className="mt-6 relative z-10"
                                  >
                                    <motion.div 
                                      animate={{ boxShadow: ['0 8px 30px rgba(147, 51, 234, 0.12)', '0 8px 40px rgba(147, 51, 234, 0.2)', '0 8px 30px rgba(147, 51, 234, 0.12)'] }}
                                      transition={{ duration: 3, repeat: Infinity }}
                                      className="bg-white border border-purple-200 rounded-2xl p-5 shadow-xl"
                                    >
                                      <div className="flex items-start gap-4">
                                        <motion.div 
                                          animate={{ 
                                            boxShadow: ['0 0 0 0 rgba(147, 51, 234, 0.3)', '0 0 0 8px rgba(147, 51, 234, 0)', '0 0 0 0 rgba(147, 51, 234, 0.3)']
                                          }}
                                          transition={{ duration: 2, repeat: Infinity }}
                                          className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/30"
                                        >
                                          <Sparkles className="w-5 h-5 text-white" />
                                        </motion.div>
                                        <div className="flex-1">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="font-bold text-gray-900">Lexy Insight</span>
                                            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full font-medium">AI Suggestion</span>
                                          </div>
                                          <p className="text-sm text-gray-600 leading-relaxed mb-4">
                                            Tech industry standard NDA duration is <span className="font-semibold text-purple-700">5 years</span>. Extended confidentiality period provides better IP protection for technology companies.
                                          </p>
                                          <div className="flex gap-3">
                                            <motion.button 
                                              whileHover={{ scale: 1.02 }}
                                              whileTap={{ scale: 0.98 }}
                                              className="flex-1 text-sm bg-gradient-to-r from-purple-500 to-purple-600 text-white py-2.5 rounded-xl font-semibold shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                                            >
                                              Accept Change
                                            </motion.button>
                                            <button className="flex-1 text-sm border border-gray-200 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-colors text-gray-600">
                                              Dismiss
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </motion.div>
                                  </motion.div>
                                </motion.div>

                                <motion.p
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 3.5 }}
                                  className="text-[15px] leading-7"
                                >
                                  <span className="font-bold">3.1 Permitted Disclosure.</span> Confidential information may only be disclosed to{' '}
                                  <motion.span
                                    initial={{ backgroundColor: 'transparent' }}
                                    animate={{ backgroundColor: 'rgb(254 249 195)' }}
                                    transition={{ delay: 4, duration: 0.5 }}
                                    className="px-1.5 py-0.5 rounded"
                                  >
                                    employees with a need-to-know basis
                                  </motion.span>
                                  {' '}who have signed confidentiality agreements.
                                </motion.p>

                                <motion.div
                                  initial={{ opacity: 0, y: 15 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 4.5 }}
                                  className="bg-gradient-to-r from-purple-50 via-purple-50/80 to-purple-50/50 border-l-4 border-purple-500 p-5 rounded-r-xl mt-10"
                                >
                                  <div className="flex items-center gap-2.5 text-purple-700 font-bold mb-2">
                                    <motion.div
                                      animate={{ rotate: [0, 360] }}
                                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                    >
                                      <Zap className="w-5 h-5" />
                                    </motion.div>
                                    AI Review Complete
                                  </div>
                                  <p className="text-sm text-purple-600 font-medium">
                                    3 improvements made • Compliance verified • Ready for signature
                                  </p>
                                </motion.div>
                              </motion.div>
                            </div>
                          </div>
                        </div>

                        {/* Lexy Sidebar Panel - Enhanced */}
                        <motion.div
                          initial={{ x: 120, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.3, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                          className="w-80 bg-gradient-to-b from-[#0f0f12] to-[#1a1a1f] text-white flex flex-col"
                        >
                          <div className="p-5 border-b border-white/10">
                            <div className="flex items-center gap-3">
                              <motion.div 
                                animate={{ boxShadow: ['0 0 15px rgba(147, 51, 234, 0.3)', '0 0 25px rgba(147, 51, 234, 0.5)', '0 0 15px rgba(147, 51, 234, 0.3)'] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center"
                              >
                                <Sparkles className="w-6 h-6" />
                              </motion.div>
                              <div>
                                <span className="font-bold text-lg">Lexy</span>
                                <p className="text-xs text-gray-400">Document Intelligence</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex-1 p-4 space-y-3 overflow-auto">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-3">Analysis Results</p>
                            {[
                              { type: 'success', title: 'Duration Extended', text: 'Confidentiality period updated to industry standard (5 years)', icon: CheckCircle },
                              { type: 'warning', title: 'Clause Review', text: 'Consider adding mutual NDA provisions', icon: AlertCircle },
                              { type: 'info', title: 'Best Practice', text: 'Add jurisdiction clause for enforceability', icon: Target },
                            ].map((item, i) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, x: 25 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 1.5 + i * 0.35 }}
                                className={`p-4 rounded-xl text-sm backdrop-blur-sm ${
                                  item.type === 'success' ? 'bg-green-500/10 border border-green-500/20' :
                                  item.type === 'warning' ? 'bg-amber-500/10 border border-amber-500/20' :
                                  'bg-blue-500/10 border border-blue-500/20'
                                }`}
                              >
                                <div className={`flex items-center gap-2.5 font-semibold mb-2 ${
                                  item.type === 'success' ? 'text-green-400' :
                                  item.type === 'warning' ? 'text-amber-400' :
                                  'text-blue-400'
                                }`}>
                                  <item.icon className="w-4 h-4" />
                                  {item.title}
                                </div>
                                <p className="text-gray-400 text-xs leading-relaxed">{item.text}</p>
                              </motion.div>
                            ))}
                          </div>
                          
                          <div className="p-4 border-t border-white/10 space-y-3">
                            <motion.button 
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-3.5 rounded-xl transition-all shadow-lg shadow-purple-500/25"
                            >
                              Apply All Suggestions
                            </motion.button>
                            <button className="w-full text-gray-400 hover:text-white font-medium py-2.5 rounded-xl transition-colors text-sm">
                              Review One by One
                            </button>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}

                  {/* TIME TRACKING & INVOICING SCENE - Enhanced */}
                  {currentScene === 'time-tracking' && (
                    <motion.div
                      key="time-tracking"
                      initial={{ opacity: 0, scale: 0.9, y: 30 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 1.05, y: -30 }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full grid grid-cols-2 gap-5"
                    >
                      {/* Left: Time Tracking - Enhanced */}
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg flex flex-col overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-white to-emerald-50/30">
                          <div className="flex items-center gap-4">
                            <motion.div 
                              animate={{ boxShadow: ['0 0 0 0 rgba(16, 185, 129, 0.2)', '0 0 0 8px rgba(16, 185, 129, 0)', '0 0 0 0 rgba(16, 185, 129, 0.2)'] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30"
                            >
                              <Timer className="w-6 h-6 text-white" />
                            </motion.div>
                            <div>
                              <h3 className="font-bold text-gray-900 text-lg">Time Tracker</h3>
                              <p className="text-sm text-gray-500">Automatic capture</p>
                            </div>
                          </div>
                          <motion.div
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="flex items-center gap-2.5 text-emerald-700 bg-emerald-100 px-4 py-2 rounded-full font-semibold"
                          >
                            <motion.span 
                              animate={{ opacity: [1, 0.4, 1] }}
                              transition={{ duration: 1, repeat: Infinity }}
                              className="w-2.5 h-2.5 rounded-full bg-emerald-500"
                            />
                            <span className="text-sm">Live</span>
                          </motion.div>
                        </div>
                        
                        <div className="flex-1 p-6 overflow-auto">
                          {/* Active Timer - More prominent */}
                          <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 rounded-2xl p-6 text-white mb-6 shadow-xl shadow-emerald-500/30"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <span className="text-emerald-100 text-sm font-medium">Currently tracking</span>
                              <span className="text-xs bg-white/25 px-3 py-1.5 rounded-full font-semibold backdrop-blur-sm">Contract Review</span>
                            </div>
                            <motion.div
                              animate={{ textShadow: ['0 0 20px rgba(255,255,255,0.3)', '0 0 40px rgba(255,255,255,0.5)', '0 0 20px rgba(255,255,255,0.3)'] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="text-5xl font-mono font-bold tracking-wider mb-3"
                            >
                              01:23:45
                            </motion.div>
                            <div className="flex items-center justify-between">
                              <p className="text-emerald-100 text-sm">Nordic Tech AS • NDA Review</p>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="text-xs bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg font-semibold transition-colors"
                              >
                                Pause
                              </motion.button>
                            </div>
                          </motion.div>

                          {/* Time Entries - Enhanced */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between mb-4">
                              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Today's Activity</p>
                              <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 px-2.5 py-1 rounded-full">4.0h logged</span>
                            </div>
                            {[
                              { client: 'Acme Corporation', task: 'Document Review', time: '2.5h', amount: '$375', color: 'from-blue-500 to-blue-600' },
                              { client: 'Nordic Tech', task: 'Client Consultation', time: '0.5h', amount: '$75', color: 'from-purple-500 to-purple-600' },
                              { client: 'Fjord Shipping', task: 'Contract Drafting', time: '1.0h', amount: '$150', color: 'from-amber-500 to-amber-600' },
                            ].map((entry, i) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -15 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.8 + i * 0.15 }}
                                className="flex items-center justify-between p-4 bg-gray-50/80 rounded-xl hover:bg-gray-100/80 transition-colors group cursor-pointer"
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-1.5 h-10 rounded-full bg-gradient-to-b ${entry.color}`} />
                                  <div>
                                    <p className="font-semibold text-gray-900 text-sm">{entry.task}</p>
                                    <p className="text-xs text-gray-500">{entry.client}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-gray-900 text-sm">{entry.time}</p>
                                  <p className="text-xs text-emerald-600 font-semibold">{entry.amount}</p>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right: Invoicing - Enhanced */}
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg flex flex-col overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-white to-purple-50/30">
                          <div className="flex items-center gap-4">
                            <motion.div 
                              animate={{ rotate: [0, 5, 0, -5, 0] }}
                              transition={{ duration: 4, repeat: Infinity }}
                              className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30"
                            >
                              <FileText className="w-6 h-6 text-white" />
                            </motion.div>
                            <div>
                              <h3 className="font-bold text-gray-900 text-lg">Smart Invoicing</h3>
                              <p className="text-sm text-gray-500">One-click billing</p>
                            </div>
                          </div>
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.8, type: "spring" }}
                            className="text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-semibold"
                          >
                            Ready to send
                          </motion.span>
                        </div>
                        
                        <div className="flex-1 p-6 overflow-auto">
                          <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm"
                          >
                            {/* Invoice Header - Professional */}
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100/80 px-6 py-5 border-b border-gray-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Invoice</p>
                                  <p className="font-bold text-gray-900 text-lg">#INV-2025-0042</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-500 mb-1">Due Date</p>
                                  <p className="font-semibold text-gray-900">Jan 30, 2025</p>
                                </div>
                              </div>
                            </div>

                            {/* Invoice Details - Enhanced */}
                            <div className="p-6 space-y-5">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Bill To</p>
                                  <p className="font-semibold text-gray-900">Nordic Tech AS</p>
                                  <p className="text-xs text-gray-500">Oslo, Norway</p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Period</p>
                                  <p className="font-semibold text-gray-900">Dec 1-15, 2025</p>
                                  <p className="text-xs text-gray-500">16.5 billable hours</p>
                                </div>
                              </div>
                              
                              <div className="border-t border-gray-100 pt-5 space-y-3">
                                {[
                                  { desc: 'Contract Review & Analysis', hours: '8.5h', rate: '$150', amount: '$1,275' },
                                  { desc: 'Legal Consultation', hours: '3.0h', rate: '$150', amount: '$450' },
                                  { desc: 'Document Drafting', hours: '5.0h', rate: '$150', amount: '$750' },
                                ].map((line, i) => (
                                  <motion.div
                                    key={i}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.3 + i * 0.12 }}
                                    className="flex justify-between text-sm items-center"
                                  >
                                    <span className="text-gray-700 font-medium">{line.desc}</span>
                                    <div className="flex gap-6 text-right">
                                      <span className="text-gray-400 w-14">{line.hours}</span>
                                      <span className="font-bold text-gray-900 w-20">{line.amount}</span>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                              
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 2 }}
                                className="border-t-2 border-gray-900 pt-4 flex justify-between items-center"
                              >
                                <span className="font-bold text-gray-900 text-lg">Total Due</span>
                                <motion.span
                                  initial={{ scale: 0.8 }}
                                  animate={{ scale: 1 }}
                                  transition={{ delay: 2.2, type: "spring" }}
                                  className="font-black text-2xl text-gray-900"
                                >
                                  $2,475
                                </motion.span>
                              </motion.div>
                            </div>
                          </motion.div>

                          <motion.button
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ delay: 2.5 }}
                            className="w-full mt-5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-purple-500/30 flex items-center justify-center gap-2.5"
                          >
                            <Send className="w-5 h-5" />
                            Send Invoice
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO - Minimalist World-Class Final CTA
      ═══════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {currentScene === 'hero' && (
          <WorldClassLandingPage navigate={navigate} mousePos={mousePos} />
        )}
      </AnimatePresence>
    </div>
    </MouseContext.Provider>
  );
};

export default HeroAnimatedV3;

